import { Link, NavLink } from "react-router-dom";
import { useState, useRef, useEffect } from "react";
import { Menu, X, BookHeart, LogOut, User, Heart } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

const links = [
  { to: "/", label: "Home", testid: "nav-home" },
  { to: "/books", label: "Books", testid: "nav-books" },
  { to: "/coloring", label: "Coloring", testid: "nav-coloring" },
  { to: "/about", label: "About", testid: "nav-about" },
  { to: "/blog", label: "Blog", testid: "nav-blog" },
  { to: "/contact", label: "Contact", testid: "nav-contact" },
];

function GoogleIcon({ size = 16 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 48 48" aria-hidden="true">
      <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/>
      <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/>
      <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"/>
      <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/>
      <path fill="none" d="M0 0h48v48H0z"/>
    </svg>
  );
}

function ReaderMenu({ user, onLogout }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const onClick = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const initials = (user.name || user.email || "R").trim().charAt(0).toUpperCase();

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="flex items-center gap-2 pl-1 pr-3 py-1 rounded-full border transition-colors"
        style={{ borderColor: "#C7B5FF", background: "#FFFFFF" }}
        data-testid="reader-menu-toggle"
      >
        {user.picture ? (
          <img
            src={user.picture}
            alt={user.name || "profile"}
            className="w-8 h-8 rounded-full object-cover"
            referrerPolicy="no-referrer"
          />
        ) : (
          <span
            className="w-8 h-8 rounded-full flex items-center justify-center font-fredoka text-sm"
            style={{ background: "#FF6B9D", color: "#fff" }}
          >
            {initials}
          </span>
        )}
        <span className="font-fredoka text-sm max-w-[7rem] truncate" style={{ color: "#2D3055" }}>
          {user.name?.split(" ")[0] || "Reader"}
        </span>
      </button>
      {open && (
        <div
          className="absolute right-0 mt-2 w-56 rounded-2xl overflow-hidden border shadow-lg z-50"
          style={{ borderColor: "#C7B5FF", background: "#FFFFFF" }}
          data-testid="reader-menu"
        >
          <div className="px-4 py-3 border-b" style={{ borderColor: "#F0EBFF" }}>
            <p className="font-fredoka text-sm truncate" style={{ color: "#2D3055" }}>
              {user.name || "Reader"}
            </p>
            <p className="text-xs font-nunito truncate" style={{ color: "#7E82A1" }}>{user.email}</p>
          </div>
          <Link
            to="/my/library"
            onClick={() => setOpen(false)}
            className="w-full flex items-center gap-2 px-4 py-3 text-left font-fredoka text-sm hover:bg-[#FFF3F8]"
            style={{ color: "#2D3055" }}
            data-testid="reader-my-library"
          >
            <Heart size={16} style={{ color: "#FF6B9D" }} fill="#FF6B9D" /> My Library
          </Link>
          <button
            type="button"
            onClick={() => { setOpen(false); onLogout(); }}
            className="w-full flex items-center gap-2 px-4 py-3 text-left font-fredoka text-sm hover:bg-[#FFF3F8]"
            style={{ color: "#2D3055" }}
            data-testid="reader-logout"
          >
            <LogOut size={16} style={{ color: "#FF6B9D" }} /> Sign out
          </button>
        </div>
      )}
    </div>
  );
}

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const { user, signInWithGoogle, logout } = useAuth();
  const reader = user && user.role === "reader" ? user : null;

  return (
    <header
      className="sticky top-0 z-40 backdrop-blur-xl border-b"
      style={{ background: "rgba(253, 251, 247, 0.82)", borderColor: "#C7B5FF" }}
      data-testid="site-navbar"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-10 py-4 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2 group" data-testid="nav-logo">
          <span className="w-10 h-10 rounded-2xl flex items-center justify-center"
            style={{ background: "#FF6B9D", color: "#fff", boxShadow: "0 8px 20px rgba(224,122,95,0.25)" }}>
            <BookHeart size={22} strokeWidth={2.2} />
          </span>
          <span className="flex flex-col leading-none">
            <span className="font-fredoka text-xl font-semibold tracking-tight" style={{ color: "#2D3055" }}>MKBunchBooks<span style={{ color: "#FF6B9D" }}>.com</span></span>
            <span className="font-caveat text-base" style={{ color: "#FF6B9D" }}>adventures &amp; wonders for little hearts</span>
          </span>
        </Link>

        <nav className="hidden md:flex items-center gap-1">
          {links.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              end={l.to === "/"}
              data-testid={l.testid}
              className={({ isActive }) =>
                `font-fredoka px-4 py-2 rounded-full text-[15px] transition-colors ${
                  isActive ? "text-white" : "hover:bg-[#E7EEFF]"
                }`
              }
              style={({ isActive }) => ({
                background: isActive ? "#6EC4E8" : "transparent",
                color: isActive ? "#fff" : "#2D3055",
              })}
            >
              {l.label}
            </NavLink>
          ))}

          {reader ? (
            <div className="ml-2">
              <ReaderMenu user={reader} onLogout={logout} />
            </div>
          ) : (
            <button
              type="button"
              onClick={signInWithGoogle}
              className="ml-2 inline-flex items-center gap-2 pl-3 pr-4 py-2 rounded-full border font-fredoka text-sm transition-transform hover:-translate-y-0.5"
              style={{ background: "#FFFFFF", color: "#2D3055", borderColor: "#C7B5FF", boxShadow: "0 6px 14px rgba(45,48,85,0.06)" }}
              data-testid="google-signin-btn"
            >
              <GoogleIcon size={16} />
              Sign in with Google
            </button>
          )}

          {/* Admin login is intentionally NOT linked from the public nav.
              Go directly to /admin/login to sign in. */}
        </nav>

        <button
          className="md:hidden p-2 rounded-full hover:bg-[#E7EEFF]"
          onClick={() => setOpen((o) => !o)}
          aria-label="Toggle menu"
          data-testid="nav-mobile-toggle"
        >
          {open ? <X /> : <Menu />}
        </button>
      </div>

      {open && (
        <div className="md:hidden border-t px-6 py-4 flex flex-col gap-2" style={{ borderColor: "#C7B5FF" }}>
          {links.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              end={l.to === "/"}
              onClick={() => setOpen(false)}
              className="font-fredoka px-4 py-3 rounded-2xl"
              style={{ color: "#2D3055", background: "#E7EEFF" }}
              data-testid={`${l.testid}-mobile`}
            >
              {l.label}
            </NavLink>
          ))}

          {reader ? (
            <>
              <Link
                to="/my/library"
                onClick={() => setOpen(false)}
                className="font-fredoka px-4 py-3 rounded-2xl inline-flex items-center gap-2"
                style={{ color: "#2D3055", background: "#FFE1EC" }}
                data-testid="reader-my-library-mobile"
              >
                <Heart size={14} fill="#FF6B9D" stroke="#FF6B9D" /> My Library
              </Link>
              <div className="rounded-2xl border px-4 py-3 flex items-center justify-between" style={{ borderColor: "#C7B5FF" }}>
              <div className="flex items-center gap-2 min-w-0">
                {reader.picture ? (
                  <img src={reader.picture} alt="" className="w-8 h-8 rounded-full object-cover" referrerPolicy="no-referrer" />
                ) : (
                  <span className="w-8 h-8 rounded-full flex items-center justify-center font-fredoka text-sm" style={{ background: "#FF6B9D", color: "#fff" }}>
                    <User size={14} />
                  </span>
                )}
                <span className="font-fredoka text-sm truncate" style={{ color: "#2D3055" }}>
                  {reader.name || reader.email}
                </span>
              </div>
              <button
                type="button"
                onClick={() => { setOpen(false); logout(); }}
                className="font-fredoka text-sm inline-flex items-center gap-1"
                style={{ color: "#FF6B9D" }}
                data-testid="reader-logout-mobile"
              >
                <LogOut size={14} /> Sign out
              </button>
            </div>
            </>
          ) : (
            <button
              type="button"
              onClick={() => { setOpen(false); signInWithGoogle(); }}
              className="font-fredoka px-4 py-3 rounded-2xl border inline-flex items-center gap-2 justify-center"
              style={{ background: "#FFFFFF", color: "#2D3055", borderColor: "#C7B5FF" }}
              data-testid="google-signin-btn-mobile"
            >
              <GoogleIcon size={16} />
              Sign in with Google
            </button>
          )}

          {/* Mobile: Admin login link intentionally hidden. Go directly to
              /admin/login to sign in. */}
        </div>
      )}
    </header>
  );
}
